import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { money, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Users, PowerOff, Pencil, Sliders, Trash2, ShieldCheck, ShieldAlert } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import AdjustmentsDialog from "@/components/AdjustmentsDialog";
import SaldoProgressBar from "@/components/SaldoProgressBar";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";

// Today's date in America/Mexico_City as YYYY-MM-DD (so form defaults match
// the CDMX calendar regardless of the user's browser timezone).
const todayMX = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "America/Mexico_City" });

const emptyEmp = {
  company_id: "", full_name: "", curp: "", clabe: "",
  monthly_salary: 15000, period_days: 15,
  work_days_per_week: 5,
  hire_date: todayMX(),
  last_payout_date: todayMX(),
  email: "",
  phone: "",
};

export default function Empleados() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [companies, setCompanies] = useState([]);
  const [filterCo, setFilterCo] = useState("all");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyEmp);
  const [editing, setEditing] = useState(null);
  const [adjustFor, setAdjustFor] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { emp, impact }

  const onSaldoChanged = (eid, newSaldo) => {
    setList((prev) => prev.map((e) => (e.id === eid ? { ...e, saldo: newSaldo } : e)));
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = filterCo && filterCo !== "all" ? { company_id: filterCo } : {};
      const { data } = await api.get("/employees", { params });
      setList(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/companies");
        setCompanies(data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  useEffect(() => { load(); }, [filterCo]);

  const openCreate = () => {
    const defaultCo = isAdmin ? (companies[0]?.id || "") : user?.company_id;
    setEditing(null);
    setForm({ ...emptyEmp, company_id: defaultCo });
    setOpen(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      ...emptyEmp,
      ...e,
      work_days_per_week: e.work_days_per_week || 5,
      hire_date: e.hire_date?.slice(0, 10),
      last_payout_date: e.last_payout_date?.slice(0, 10),
    });
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!/^\d{18}$/.test(form.clabe || "")) {
      toast.error("La CLABE debe tener exactamente 18 dígitos numéricos.");
      return;
    }
    if (!/^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z\d]{2}$/.test(form.curp || "")) {
      toast.error("La CURP debe tener exactamente 18 caracteres en formato válido.");
      return;
    }
    try {
      const payload = {
        company_id: form.company_id,
        full_name: form.full_name,
        curp: form.curp,
        clabe: form.clabe,
        email: form.email || null,
        phone: form.phone || null,
        monthly_salary: Number(form.monthly_salary),
        period_days: Number(form.period_days),
        work_days_per_week: Number(form.work_days_per_week) || 5,
        hire_date: form.hire_date,
        last_payout_date: form.last_payout_date || form.hire_date,
        requests_cfdi: !!form.requests_cfdi,
      };
      if (editing) {
        delete payload.company_id;
        await api.put(`/employees/${editing.id}`, payload);
        toast.success("Empleado actualizado");
      } else {
        await api.post("/employees", payload);
        toast.success("Empleado creado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleActive = async (e) => {
    try {
      if (e.active) await api.delete(`/employees/${e.id}`);
      else await api.put(`/employees/${e.id}`, { active: true });
      toast.success(e.active ? "Empleado desactivado" : "Empleado reactivado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Anexo B — individual authorization. Admin-only.
  const toggleAuthorization = async (e) => {
    const next = !e.authorization_signed;
    const msg = next
      ? "¿Marcar al empleado como AUTORIZADO (Anexo B firmado)?"
      : "¿Quitar la autorización firmada? Mientras esté sin firmar no se podrán capturar ni aprobar adelantos para este empleado.";
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/employees/${e.id}/authorization`, { signed: next });
      toast.success(next ? "Autorización firmada registrada" : "Autorización revocada");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const askDelete = async (emp) => {
    try {
      const { data } = await api.get(`/employees/${emp.id}/delete-impact`);
      setDeleteTarget({ emp, impact: data });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/employees/${deleteTarget.emp.id}/permanent`);
      toast.success("Empleado eliminado");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-5" data-testid="empleados-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#0F172A]">Empleados</h1>
          <p className="text-sm text-[#64748B] mt-1">Administra empleados y sus saldos devengados.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-employee-btn" onClick={openCreate} className="bg-[#1D9E75] hover:bg-[#147D5C]">
              <Plus className="w-4 h-4 mr-1.5" />Nuevo empleado
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editing ? "Editar empleado" : "Nuevo empleado"}</DialogTitle></DialogHeader>
            <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {isAdmin && !editing && (
                <div className="md:col-span-2 space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-[#475569]">Empresa</Label>
                  <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                    <SelectTrigger data-testid="emp-company-select"><SelectValue placeholder="Seleccionar empresa" /></SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Field label="Nombre completo" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} required testId="emp-name" />
              <Field label="Correo electrónico" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="emp-email" />
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-[#475569]">Teléfono celular</Label>
                <Input
                  data-testid="emp-phone"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  pattern="\d{10}"
                  placeholder="10 dígitos"
                  value={form.phone ?? ""}
                  onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-[#475569]">CURP</Label>
                <Input
                  data-testid="emp-curp"
                  type="text"
                  maxLength={18}
                  required
                  aria-invalid={form.curp && form.curp.length > 0 && !/^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z\d]{2}$/.test(form.curp)}
                  value={form.curp ?? ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      curp: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 18),
                    })
                  }
                />
                {form.curp && form.curp.length > 0 && !/^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z\d]{2}$/.test(form.curp) ? (
                  <p data-testid="emp-curp-error" className="text-[11px] text-[#B91C1C] leading-snug">
                    La CURP debe tener exactamente 18 caracteres en formato válido.
                  </p>
                ) : (
                  <p className="text-[11px] text-[#64748B] leading-snug">
                    18 caracteres: 4 letras + 6 dígitos (AAMMDD) + H/M + 5 letras + 2 alfanuméricos.
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-[#475569]">CLABE interbancaria</Label>
                <Input
                  data-testid="emp-clabe"
                  type="text"
                  inputMode="numeric"
                  maxLength={18}
                  pattern="\d{18}"
                  required
                  aria-invalid={form.clabe && form.clabe.length > 0 && !/^\d{18}$/.test(form.clabe)}
                  value={form.clabe ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, clabe: e.target.value.replace(/\D/g, "").slice(0, 18) })
                  }
                />
                {form.clabe && form.clabe.length > 0 && !/^\d{18}$/.test(form.clabe) ? (
                  <p data-testid="emp-clabe-error" className="text-[11px] text-[#B91C1C] leading-snug">
                    La CLABE debe tener exactamente 18 dígitos numéricos.
                  </p>
                ) : (
                  <p className="text-[11px] text-[#64748B] leading-snug">
                    18 dígitos numéricos, sin espacios ni letras.
                  </p>
                )}
              </div>
              <Field label="Salario mensual neto" type="number" value={form.monthly_salary} onChange={(v) => setForm({ ...form, monthly_salary: v })} required testId="emp-salary" hint="Captura el monto que el empleado recibe en su cuenta después de todas las deducciones de ley (ISR, IMSS, INFONAVIT)." />
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-[#475569]">Días del periodo</Label>
                <Select value={String(form.period_days)} onValueChange={(v) => setForm({ ...form, period_days: Number(v) })}>
                  <SelectTrigger data-testid="emp-period-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 (semanal)</SelectItem>
                    <SelectItem value="15">15 (quincenal)</SelectItem>
                    <SelectItem value="30">30 (mensual)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-[#475569]">Días de trabajo por semana</Label>
                <Select value={String(form.work_days_per_week)} onValueChange={(v) => setForm({ ...form, work_days_per_week: Number(v) })}>
                  <SelectTrigger data-testid="emp-workdays-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 días (lunes a viernes)</SelectItem>
                    <SelectItem value="6">6 días (lunes a sábado)</SelectItem>
                    <SelectItem value="7">7 días (todos los días)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-[#64748B] leading-snug">
                  Determina cómo se reparte el salario mensual entre los días efectivos de trabajo para calcular el saldo devengado con mayor precisión.
                </p>
              </div>
              <Field label="Fecha de ingreso" type="date" value={form.hire_date} onChange={(v) => setForm({ ...form, hire_date: v })} required testId="emp-hire" />
              <Field label="Último pago de nómina" type="date" value={form.last_payout_date} onChange={(v) => setForm({ ...form, last_payout_date: v })} testId="emp-last-payout" />
              <div className="md:col-span-2 space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-[#475569]">Facturación de comisiones</Label>
                <label className="flex items-start gap-2 rounded-md border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2 cursor-pointer hover:bg-[#F1F5F9]">
                  <input
                    type="checkbox"
                    data-testid="emp-requests-cfdi"
                    checked={!!form.requests_cfdi}
                    onChange={(e) => setForm({ ...form, requests_cfdi: e.target.checked })}
                    className="mt-0.5 accent-[#1D9E75]"
                  />
                  <span className="text-sm text-[#0F172A]">
                    <strong>Solicita CFDI por las comisiones cobradas.</strong>
                    <span className="block text-[11px] text-[#64748B] leading-snug mt-0.5">
                      Si está activo, aparecerá marcado "Sí" en el reporte fiscal mensual para que el contador emita el CFDI.
                    </span>
                  </span>
                </label>
              </div>
              <DialogFooter className="md:col-span-2 mt-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button
                  type="submit"
                  data-testid="emp-submit-btn"
                  disabled={
                    !/^\d{18}$/.test(form.clabe || "") ||
                    !/^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z\d]{2}$/.test(form.curp || "")
                  }
                  className="bg-[#1D9E75] hover:bg-[#147D5C] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editing ? "Guardar" : "Crear empleado"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isAdmin && (
        <Card className="p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Label className="text-xs uppercase tracking-wider text-[#475569]">Filtrar por empresa</Label>
            <Select value={filterCo} onValueChange={setFilterCo}>
              <SelectTrigger data-testid="filter-company" className="max-w-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las empresas</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </Card>
      )}

      <Card className="overflow-hidden p-0">
        {loading ? (
          <div className="p-8 flex justify-center"><div className="nomi-spin" /></div>
        ) : list.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-10 h-10 text-[#CBD5E1] mx-auto" />
            <div className="mt-3 text-sm text-[#64748B]">No hay empleados</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="empleados-table">
              <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#64748B]">
                  <th className="px-4 py-3 font-medium">Empleado</th>
                  <th className="px-4 py-3 font-medium">CURP</th>
                  <th className="px-4 py-3 font-medium text-right">Salario mensual neto</th>
                  <th className="px-4 py-3 font-medium">Período</th>
                  <th className="px-4 py-3 font-medium text-right">Saldo devengado</th>
                  <th className="px-4 py-3 font-medium text-right">Máx. 30%</th>
                  <th className="px-4 py-3 font-medium">Autorización</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {list.map((e) => (
                  <tr key={e.id} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC]">
                    <td className="px-4 py-3">
                      <Link to={`/reportes?emp=${e.id}`} className="font-medium text-[#0F172A] hover:text-[#1D9E75]">
                        {e.full_name}
                      </Link>
                      <div className="text-xs text-[#64748B] font-mono">{e.clabe}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{e.curp}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{money(e.monthly_salary)}</td>
                    <td className="px-4 py-3">{e.period_days}d</td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-[#147D5C]">
                      {money(e.saldo?.gross_accrued)}
                      <div className="mt-1.5 min-w-[180px] ml-auto text-left">
                        <SaldoProgressBar saldo={e.saldo} compact />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-[#475569]">{money(e.saldo?.max_allowed_30pct)}</td>
                    <td className="px-4 py-3">
                      {e.authorization_signed ? (
                        <span
                          data-testid={`auth-badge-${e.id}`}
                          title={e.authorization_signed_at ? `Firmada el ${formatDate(e.authorization_signed_at)}` : "Firmada"}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#D1FAE5] text-[#147D5C] text-xs font-semibold"
                        >
                          <ShieldCheck className="w-3 h-3" /> Firmada
                        </span>
                      ) : (
                        <span
                          data-testid={`auth-badge-${e.id}`}
                          title="Falta la autorización firmada del empleado"
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold"
                        >
                          <ShieldAlert className="w-3 h-3" /> Pendiente
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={e.active ? "ACTIVA" : "INACTIVA"} /></td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline" data-testid={`adjust-emp-${e.id}`} onClick={() => setAdjustFor(e)} title="Registrar ajuste">
                          <Sliders className="w-3.5 h-3.5" />
                        </Button>
                        {isAdmin && (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`auth-toggle-${e.id}`}
                            onClick={() => toggleAuthorization(e)}
                            title={e.authorization_signed ? "Revocar autorización firmada" : "Marcar autorización como firmada"}
                            className={e.authorization_signed ? "text-[#147D5C] border-[#86EFAC]" : "text-amber-700 border-amber-300 bg-amber-50 hover:bg-amber-100"}
                          >
                            {e.authorization_signed
                              ? <ShieldCheck className="w-3.5 h-3.5" />
                              : <ShieldAlert className="w-3.5 h-3.5" />}
                          </Button>
                        )}
                        <Button size="sm" variant="outline" data-testid={`edit-emp-${e.id}`} onClick={() => openEdit(e)}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" data-testid={`toggle-emp-${e.id}`} onClick={() => toggleActive(e)} title={e.active ? "Desactivar" : "Reactivar"}>
                          <PowerOff className="w-3.5 h-3.5" />
                        </Button>
                        {isAdmin && (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`delete-emp-${e.id}`}
                            onClick={() => askDelete(e)}
                            title="Eliminar permanentemente"
                            className="text-[#B91C1C] hover:bg-rose-50 border-rose-200"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <AdjustmentsDialog
        employee={adjustFor}
        open={!!adjustFor}
        onOpenChange={(v) => !v && setAdjustFor(null)}
        onSaldoChanged={onSaldoChanged}
      />

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Eliminar empleado"
        description={
          deleteTarget && (
            <>
              ¿Estás seguro de que deseas eliminar a{" "}
              <strong>{deleteTarget.emp.full_name}</strong>?
            </>
          )
        }
        warnings={(() => {
          if (!deleteTarget?.impact) return [];
          const w = [];
          const i = deleteTarget.impact;
          if (i.open_advances > 0) {
            w.push(`Tiene ${i.open_advances} adelanto(s) en proceso (no podrás eliminar hasta liquidarlos).`);
          }
          if (i.total_advances > 0) {
            w.push(`Se eliminarán ${i.total_advances} adelanto(s) histórico(s) y todos sus ajustes.`);
          }
          if (i.active === false) {
            w.push("El empleado está inactivo (soft-deleted).");
          }
          return w;
        })()}
        confirmLabel="Eliminar empleado"
        busyLabel="Eliminando..."
        onConfirm={confirmDelete}
        testId="confirm-delete-employee"
      />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testId, hint }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-[#475569]">{label}</Label>
      <Input data-testid={testId} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} required={required} />
      {hint && (
        <p className="text-[11px] text-[#64748B] leading-snug">{hint}</p>
      )}
    </div>
  );
}
